import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchQuery } from '@platform/state/api';
import { Icon, Segmented } from '@platform/ui';
import { LinkSearchDialog } from '../lookup/LinkSearchDialog';
import { LookupFooter } from '../lookup/LookupFooter';
import { useDebouncedValue } from '../lookup/useDebouncedValue';
import type { IMdToolbarProps } from './IMdToolbarProps';
import { MD_COLOURS } from './mdColours';
import { MD_HIGHLIGHT } from './mdHighlight';
import { MdMenu } from './MdMenu';
import type { MdMode } from './MdMode';

const BT = '`';

/**
 * Панель форматування статті (UIUX/field-types/rich-text; прототип kbTools): H2 · H3 · Ж · К · список · нумерований · цитата · код ·
 * таблиця · колір ▾ (шість кольорів і виділення) · посилання на запис ▾ (пошук записів воркспейсу) · ⟳ живий запит; праворуч — режими
 * «Писати · Розділений · Перегляд».
 */
export function MdToolbar({ ws, mode, onMode, wrap, line, insert, selected }: IMdToolbarProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const debounced = useDebouncedValue(q.trim(), 250);
  const { data, isFetching } = useSearchQuery({ q: debounced, workspaceId: ws }, { skip: !debounced });
  const hits = (data?.groups ?? []).flatMap((g) => g.hits).slice(0, 30);
  const tools: Array<[string, string, () => void]> = [
    ['H2', t('fieldControls.mdHeading'), () => line('## ')],
    ['H3', t('fieldControls.mdSubheading'), () => line('### ')],
    [t('fieldControls.mdBoldShort'), t('fieldControls.mdBold'), () => wrap('**', '**', t('fieldControls.mdBoldText'))],
    [t('fieldControls.mdItalicShort'), t('fieldControls.mdItalic'), () => wrap('*', '*', t('fieldControls.mdItalicText'))],
    [`• ${t('fieldControls.mdList')}`, t('fieldControls.mdBulleted'), () => line('- ')],
    [`1. ${t('fieldControls.mdList')}`, t('fieldControls.mdNumbered'), () => line('1. ')],
    ['❝', t('fieldControls.mdQuote'), () => line('> ')],
    [t('fieldControls.mdCode'), t('fieldControls.mdInlineCode'), () => wrap(BT, BT, 'code')],
    [t('fieldControls.mdTable'), t('fieldControls.mdInsertTable'), () => insert(t('fieldControls.mdTableTemplate'), true)],
  ];
  const colours = [...Object.entries(MD_COLOURS).map(([k, c]) => ({ value: k, label: t(`fieldControls.mdColours.${k}`), dot: c })), { value: 'hl', label: t('fieldControls.mdHighlight'), dot: MD_HIGHLIGHT }];
  const modes: Array<{ value: MdMode; label: string }> = [{ value: 'write', label: t('fieldControls.mdWrite') }, { value: 'split', label: t('fieldControls.mdSplit') }, { value: 'preview', label: t('fieldControls.mdPreview') }];
  return (
    <div className="md-editor__bar" role="toolbar" aria-label={t('fieldControls.mdFormatting')}>
      {tools.map(([label, title, run]) => (
        <button key={title} type="button" className="md-editor__tool" title={title} aria-label={title} onMouseDown={(e) => { e.preventDefault(); run(); }}>{label}</button>
      ))}
      <MdMenu label={t('fieldControls.mdTextColour')} content={<><span className="md-editor__swatch" aria-hidden />{t('fieldControls.mdColourShort')}</>} options={colours}
        onPick={(v) => (v === 'hl' ? wrap('==', '==', t('fieldControls.mdHighlighted')) : wrap(`{${v}|`, '}', t('fieldControls.mdColoured')))} />
      <MdMenu label={t('fieldControls.mdLinkRecord')} content={<><Icon name="link" size={14} />{t('fieldControls.mdLinkShort')}</>} query={q} onQuery={setQ} loading={isFetching}
        emptyText={debounced ? t('fieldControls.noMatches') : t('fieldControls.typeRecord')}
        options={hits.map((h) => ({ value: `${h.module}/${h.recordType}/${h.recordId}`, label: h.title || h.display, sub: h.display }))}
        onPick={(v) => { const h = hits.find((x) => `${x.module}/${x.recordType}/${x.recordId}` === v); insert(`[${selected() || h?.title || h?.display || 'link'}](rec:${v})`); }}
        footer={<LookupFooter count={hits.length} total={data?.total ?? 0} onAdvanced={() => setAdvanced(true)} />} />
      {advanced && (
        <LinkSearchDialog ws={ws} onClose={() => setAdvanced(false)}
          onPick={(h) => { setAdvanced(false); insert(`[${selected() || h.title || h.display}](rec:${h.module}/${h.recordType}/${h.recordId})`); }} />
      )}
      <button type="button" className="md-editor__tool" title={t('fieldControls.mdInsertQuery')} aria-label={t('fieldControls.mdInsertQuery')}
        onMouseDown={(e) => { e.preventDefault(); insert(`${BT}${BT}${BT}query\nmodule: task\nopen: yes\nassignee: me\nlimit: 5\n${BT}${BT}${BT}\n`, true); }}>
        ⟳ {t('fieldControls.mdQuery')}
      </button>
      <span className="md-editor__spacer" />
      <Segmented options={modes} value={mode} onChange={onMode} />
    </div>
  );
}
